// src/pages/StaffTimesheets.jsx
import { useState, useEffect } from 'react';
import SidebarNav from '../../components/MasterSideBar';
import TopbarNav from '../../components/TopbarNav';
import TimesheetViewModal from '../../components/TimesheetViewModal'; 
import TimesheetFormModal from '../../components/Timesheetform'; 
import TimesheetTable from '../../components/TimesheetTable';
import { fetchMyTimesheets } from "../../api/Timesheetform-api"; 
import { FiPlus, FiCheckCircle, FiLoader, FiCalendar, FiList } from 'react-icons/fi';

const StaffTimesheets = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleFetchMyData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const data = await fetchMyTimesheets(token);
      setTimesheets(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error("Error fetching timesheets:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenViewModal = (id) => {
    const found = timesheets.find(ts => ts._id === id);
    if (found) {
      setSelectedTimesheet(found);
      setIsViewOpen(true);
    }
  };

  useEffect(() => { 
    handleFetchMyData(); 
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-x-hidden">
      <SidebarNav 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${collapsed ? "md:ml-20" : "md:ml-64"}`}> 
        <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="p-4 md:p-8 pt-20 md:pt-24 relative min-h-screen">
          
          <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-4xl font-black text-gray-800 tracking-tighter uppercase">
                My Timesheets
              </h1>
              <p className="text-sm md:text-base text-gray-500 font-medium">Log your hours and track approval status</p>
            </div>
            
            {/* Desktop Button */}
            <button
              onClick={() => setIsFormOpen(true)}
              className="hidden md:flex items-center justify-center gap-2 px-8 py-4 bg-orange-600 text-white rounded-2xl hover:bg-orange-700 shadow-xl shadow-orange-100 transition-all active:scale-95 font-bold uppercase text-sm tracking-widest"
            >
              <FiPlus size={20} /> Log New Entry
            </button>
          </header>

          {/* Stats Summary - Horizontal scroll on mobile */}
          <div className="flex md:grid md:grid-cols-3 gap-4 md:gap-6 mb-8 overflow-x-auto pb-4 md:pb-0 scrollbar-hide">
            {[
              { label: "Total Logs", val: timesheets.length, icon: <FiList />, color: "blue" },
              { label: "Recent Activity", val: timesheets[0] ? new Date(timesheets[0].createdAt).toLocaleDateString() : 'None', icon: <FiCalendar />, color: "orange" },
              { label: "System Status", val: "Active", icon: <FiCheckCircle />, color: "green" }
            ].map((stat, i) => (
              <div key={i} className="min-w-[250px] md:min-w-0 bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex items-center flex-shrink-0">
                 <div className={`p-4 rounded-2xl bg-${stat.color}-50 text-${stat.color}-600`}>
                    {stat.icon}
                 </div>
                 <div className="ml-5">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{stat.label}</p>
                    <p className={`${stat.label === 'Total Logs' ? 'text-3xl' : 'text-lg'} font-black text-gray-900 truncate`}>{stat.val}</p>
                 </div>
              </div>
            ))}
          </div>

          <section className="bg-transparent md:bg-white rounded-3xl md:shadow-sm md:border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32">
                <FiLoader className="animate-spin text-5xl text-orange-500 mb-4" />
                <p className="text-gray-400 font-black uppercase text-[10px] tracking-widest">Loading...</p>
              </div>
            ) : (
              <TimesheetTable 
                timesheets={timesheets} 
                onViewTimesheet={handleOpenViewModal}
                loading={loading}
                isApprover={false} // CRITICAL: This hides the name column
              />
            )}
          </section>

          {/* Mobile FAB */}
          <button
            onClick={() => setIsFormOpen(true)}
            className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-orange-600 text-white rounded-full shadow-2xl flex items-center justify-center z-50 active:scale-90"
          >
            <FiPlus size={28} />
          </button>
        </main>
      </div>

      {isViewOpen && (
        <TimesheetViewModal 
          timesheet={selectedTimesheet} 
          onClose={() => setIsViewOpen(false)} 
        />
      )}

      <TimesheetFormModal 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        onSubmitSuccess={() => {
          setIsFormOpen(false);
          handleFetchMyData();
        }}
      />
    </div>
  );
};

export default StaffTimesheets;